import React, { useState, useRef, useEffect } from "react";
import downArrowSvg from "assets/img/arrow-down.svg";
import CustomCheckbox from "./CustomCheckbox";

// Updated interface to match API response
interface StoreItem {
  store_id: number;
  store_name: string;
  retail_store_num: string;
}

interface StateWithStores {
  state_name: string;
  stores: StoreItem[];
}

interface StoreSelectProps {
  value: number | number[];
  states: StateWithStores[]; // Changed from stores to states
  onChange: (id: number | number[]) => void;
  multiSelect?: boolean;
  isDisabled?: boolean;
  hideHeader?: boolean;
}

const StoreSelect: React.FC<StoreSelectProps> = ({
  value,
  states,
  onChange,
  multiSelect = false,
  isDisabled = false,
  hideHeader = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());
  const [selectedStores, setSelectedStores] = useState<Set<number>>(
    new Set(Array.isArray(value) ? value : value !== -1 ? [value] : [])
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all stores from all states
  const allStores = states.flatMap(state => 
    state.stores.map(store => ({
      id: store.store_id,
      name: store.store_name,
      state: state.state_name,
      retail_store_num: store.retail_store_num
    }))
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleState = (stateName: string) => {
    const newExpanded = new Set(expandedStates);
    if (newExpanded.has(stateName)) {
      newExpanded.delete(stateName);
    } else {
      newExpanded.add(stateName);
    }
    setExpandedStates(newExpanded);
  };

  const handleSelectAll = () => {
    if (selectedStores.size === allStores.length) {
      setSelectedStores(new Set());
      onChange(multiSelect ? [] : -1);
    } else {
      const allIds = allStores.map((s) => s.id);
      setSelectedStores(new Set(allIds));
      onChange(multiSelect ? allIds : allIds[0]);
    }
  };

  const handleStateSelect = (stateName: string) => {
    const stateData = states.find(s => s.state_name === stateName);
    if (!stateData) return;

    const stateStoreIds = stateData.stores.map((s) => s.store_id);
    const allSelected = stateStoreIds.every((id) => selectedStores.has(id));

    const newSelected = new Set(selectedStores);
    if (allSelected) {
      stateStoreIds.forEach((id) => newSelected.delete(id));
    } else {
      stateStoreIds.forEach((id) => newSelected.add(id));
    }

    setSelectedStores(newSelected);
    onChange(multiSelect ? Array.from(newSelected) : Array.from(newSelected)[0] || -1);
  };

  const handleStoreSelect = (storeId: number) => {
    if (multiSelect) {
      const newSelected = new Set(selectedStores);
      if (newSelected.has(storeId)) {
        newSelected.delete(storeId);
      } else {
        newSelected.add(storeId);
      }
      setSelectedStores(newSelected);
      onChange(Array.from(newSelected));
    } else {
      setSelectedStores(new Set([storeId]));
      onChange(storeId);
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (isDisabled) return "Store Selection";
    if (selectedStores.size === 0) return "Store Selection";
    if (selectedStores.size === allStores.length) return "All Stores Selected";
    if (selectedStores.size === 1) {
      const store = allStores.find((s) => s.id === Array.from(selectedStores)[0]);
      // return store ? `${store.state} - ${store.name}` : "Store Selection";
      return store ? `${store.state} - ${store.retail_store_num}` : "Store Selection";
    }
    return `${selectedStores.size} Stores Selected`;
  };

  const filteredStates = searchTerm
    ? states.filter(
        (stateData) =>
          stateData.state_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stateData.stores.some((store) =>
            store.retail_store_num.toLowerCase().includes(searchTerm.toLowerCase())
          )
      )
    : states;

  const isStateSelected = (stateName: string) => {
    const stateData = states.find(s => s.state_name === stateName);
    if (!stateData) return false;
    const stateStoreIds = stateData.stores.map((s) => s.store_id);
    return stateStoreIds.every((id) => selectedStores.has(id));
  };

  const isStatePartiallySelected = (stateName: string) => {
    const stateData = states.find(s => s.state_name === stateName);
    if (!stateData) return false;
    const stateStoreIds = stateData.stores.map((s) => s.store_id);
    const selectedCount = stateStoreIds.filter((id) => selectedStores.has(id)).length;
    return selectedCount > 0 && selectedCount < stateStoreIds.length;
  };

  return (
    <div style={styles.inputGroup} data-qa="store-location-field-NEW-FIELD">
      {!hideHeader && <div style={styles.inputLabel}>State &gt; Store</div>}

      <div style={styles.selectWrapper} ref={dropdownRef}>
        <div 
          style={{
            ...styles.selectInput,
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer'
          }} 
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
        >
          <span style={styles.selectText}>{getDisplayText()}</span>
          <img src={downArrowSvg} alt="" />
        </div>

        {isOpen && !isDisabled && (
          <div style={styles.dropdown}>
            <div style={styles.searchWrapper}>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div style={styles.optionsList}>
              {multiSelect && (
                <div style={styles.option}>
                  <CustomCheckbox
                    checked={selectedStores.size === allStores.length}
                    onChange={handleSelectAll}
                    label={"Select All"}
                  />
                </div>
              )}

              {filteredStates.map((stateData) => (
                <div key={stateData.state_name}>
                  <div style={styles.stateOption} onClick={() => toggleState(stateData.state_name)}>
                    <div
                      style={{
                        ...styles.chevron,
                        transform: expandedStates.has(stateData.state_name) ? "rotate(0deg)" : "rotate(-90deg)",
                        mask: `url(${downArrowSvg}) no-repeat center / contain`,
                        WebkitMask: `url(${downArrowSvg}) no-repeat center / contain`,
                        backgroundColor: "#E5B42F",
                        width: 12,
                        height: 12,
                        display: "inline-block",
                      }}
                    />
                    {multiSelect && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <CustomCheckbox
                          checked={isStateSelected(stateData.state_name)}
                          indeterminate={isStatePartiallySelected(stateData.state_name)}
                          onChange={() => handleStateSelect(stateData.state_name)}
                          label={stateData.state_name}
                        />
                      </div>
                    )}
                  </div>

                  {expandedStates.has(stateData.state_name) && (
                    <div style={styles.storesList}>
                      {stateData.stores
                        .filter(
                          (store) =>
                            !searchTerm ||
                            store.retail_store_num.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((store) => (
                          <div
                            key={store.store_id}
                            style={styles.storeOption}
                            onClick={() => handleStoreSelect(store.store_id)}
                          >
                            {multiSelect && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <CustomCheckbox
                                  checked={selectedStores.has(store.store_id)}
                                  onChange={() => handleStoreSelect(store.store_id)}
                                  label={store.retail_store_num}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    fontSize: 14,
    color: "#333",
    fontWeight: 500,
    marginBottom: 8,
  },
  selectWrapper: {
    position: "relative",
  },
  selectInput: {
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: 16,
    fontSize: 15,
    backgroundColor: "#fff",
    cursor: "pointer",
    height: 44,
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  selectText: {
    color: "#666",
  },
  selectArrow: {
    color: "#666",
    transition: "transform 0.2s",
    fontSize: 12,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
    maxHeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  searchWrapper: {
    padding: 12,
    borderBottom: "1px solid #eee",
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 4,
    fontSize: 14,
    boxSizing: "border-box",
  },
  optionsList: {
    overflowY: "auto",
    maxHeight: 320,
  },
  option: {
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
  },
  stateOption: {
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    backgroundColor: "#f9f9f9",
    borderBottom: "1px solid #eee",
    fontWeight: 500,
  },
  storeOption: {
    padding: "10px 15px 10px 50px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
  },
  storesList: {
    backgroundColor: "#fff",
  },
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
    color: "#E5B42F",
    border: "1px solid #E5B42F",
    outline: "none",
  },
  chevron: {
    fontSize: 12,
    color: "#E5B42F",
    transition: "transform 0.2s",
    width: 12,
  },
  optionText: {
    fontSize: 14,
    color: "#333",
  },
};

export default StoreSelect;